/**
 * The Next card (docs/strategy/screens/02-today-daily.md, fix T1): the ONE thing this person should do right now,
 * chosen from their moment. A pure function, so web and phone show the same Next (T7).
 *
 * | Rule | Why |
 * |---|---|
 * | N1 Food-safety-critical work insists, even in service: a stop-work, an open safety problem (sanitizer failed, fridge above 41°F), an overdue cold/hot-holding, cooling or sanitizer check | These cannot wait for a quiet minute. Food Code 3-501.16(A)(2): TCS food is held "at 5°C (41°F) or less"; 4-501.114: chemical sanitizer strength must be at the label's range (Utah adopts both in R392-100) |
 * | N2 In service hours with few people on shift, NON-critical work defers, and the card says so truthfully ("Can wait: wipe the counters (due by 2:00 PM)"). A deferral never outlasts the deadline: once the check is past its late time it is no longer deferred | Deferring is a promise that there is still time. After the deadline the promise is false |
 * | N3 One short line says WHY now: "Walk-in check: due 10:00 AM, keeps cold food under 41°F." | A person who knows why does it right, and does not skip it |
 * | N4 Exactly one primary button, and it does the action (opens the log form, the fix, the check-in). Every state has one, even "all done" | S15: an action button at every moment. `primary` is not optional in the type |
 * | N5 Nothing is hidden: `more` counts everything else still open today | The card narrows the view, never the list |
 * | N1b Food at risk leads N1 (feed.ts F1-F5): a TCS reading out of its limit (feed.ts FOOD_AT_RISK_RULES) or food past its date or time comes before every other safety problem and check, after a stop-work or the person's own gate | 3-501.16(A)(2) "At 5°C (41°F) or less"; 3-501.18(A) "shall be discarded". A weak sanitizer bucket can be remixed in a minute; food already unsafe can be served in the next one |
 * | N6 A task a manager gave to this person (delegate.ts A5) comes first, after the food-safety rules above (N1): in the critical buckets it leads its bucket; non-critical, due within the hour or late, it goes ahead of the other open work and is never deferred (N2: someone asked); later today it leads what is due later. A task given to someone else is the last of its bucket for this person, says whose it is, and its button is "I'll do it" (a step-in is recorded). Nothing assigned: the order is exactly as before | Delegation orders the rest; it never lets a task outrank a fridge above 41°F |
 *
 * Signals, and where they come from (nothing invented):
 * - the day part: core `momentAt` from the location's weekly hours (the `moment` input). "Service" here means the
 *   in-hours part of the day (moment "day"). It is NOT a measured rush.
 * - people on shift: today's `onShift` list length (DayContext.onShift). It is who is scheduled/on the day's list, not
 *   who is physically present this minute. Unknown (undefined): nothing defers.
 * - `busy` is a SEAM, deliberately unfed: no app has a rush or ticket-volume signal today. When one exists, pass it and
 *   the deferral line says "After the rush". Until then nobody claims a rush.
 */
import { urgentProblem } from "./nag.ts";
import { nextStepFor } from "./openreport.ts";
import { isLate, type ExpectedCheck } from "./schedule.ts";
import { isFoodAtRiskFinding, type FeedCard, type FeedRole, type Moment } from "./feed.ts";
import type { IsoDateTime } from "./model.ts";

/** Two people or fewer on the day's shift list is "low staff". A judgment default (ASSUMED), not a legal number. */
export const LOW_STAFF_MAX = 2;

/** Templates whose miss can hurt a guest today: cold and hot holding, cooling, time control, sanitizer, cloth buckets. */
export const CRITICAL_TEMPLATES: ReadonlySet<string> = new Set(["cold_units", "temp_cook_hold", "tcs_items", "cooling", "time_control", "sanitizer", "wiping_cloths"]);

/** Short reason per template: a plain "why", with the Food Code number it rests on kept here for the reader of the code. */
const REASON: Record<string, string> = {
  // 3-501.16(A)(2): cold TCS food "at 5°C (41°F) or less".
  cold_units: "keeps cold food at 41°F or below",
  // 3-501.16(A)(1): hot TCS food "at 57°C (135°F) or above".
  temp_cook_hold: "keeps hot food at 135°F or above",
  tcs_items: "keeps food out of the danger zone",
  cooling: "cooling too slowly lets germs grow",
  time_control: "food left out too long has to be thrown away",
  // 4-501.114: chemical sanitizer at the label's strength.
  sanitizer: "sanitizer only works at the right strength",
  wiping_cloths: "a weak cloth bucket spreads germs",
  restroom_check: "so guests and staff can wash their hands",
  calibration: "every temperature is only as true as the thermometer",
  receiving: "food that arrives warm is not safe to serve",
};

/** The thing a check is about, in plain words, for the deck card's "where" line. Only what the template itself names. */
const WHERE: Record<string, string> = {
  cold_units: "Fridges and freezers", temp_cook_hold: "Hot holding", tcs_items: "Food on the line", sanitizer: "Sanitizer bucket and sinks",
  wiping_cloths: "Cloth bucket", restroom_check: "Restroom", calibration: "Thermometers", receiving: "Where deliveries come in",
};

export type NextGlyph = "thermo" | "flask" | "restroom" | "do" | "hands" | "learn" | "alert" | "check" | "report" | "write" | "clock";

export type NextAction =
  | { kind: "checkin" }
  | { kind: "learn" }
  /** Open the log form (or tick the task) for this expected check. `coolingId`: a cooling batch's reading (core cooling.ts). */
  | { kind: "check"; key: string; templateId: string; taskId?: string; coolingId?: string }
  /** Add what was done about a failed reading. */
  | { kind: "fix"; entryId: string; askId?: string }
  | { kind: "incident"; id: string }
  | { kind: "report" }
  /** Throw out a stored food and write it on the discard log (foodstore.ts foodAtRisk; Food Code 3-501.18). */
  | { kind: "throw_out"; foodId: string };

export interface NextProblem {
  id: string;
  title: string;
  /** One short line: why this one, now. */
  why: string;
  critical: boolean;
  /** N1b: food at risk now (a TCS reading out of its limit, food past its date): it leads every other problem. */
  foodRisk?: boolean;
  verb: string;
  action: NextAction;
}

export type NextKind = "stop_work" | "gate" | "problem" | "check" | "waiting" | "upcoming" | "done";

/** One check's assignment as this person reads it (delegate.ts AssignView): `line` is "Dana asked you." / "Susan's.". */
export interface NextAssign { mine: boolean; line: string }

export interface NextItem {
  /** Stable: the check's key, the problem's id, or the state name. The apps compare it to notice "that one is done". */
  id: string;
  kind: NextKind;
  tone: "stop" | "warn" | "info" | "go";
  glyph: NextGlyph;
  /** What to do, short: "Walk-in cooler check". */
  title: string;
  /** Why now, one short line (N3). */
  why: string;
  /** "10:00 AM" or "Was due 10:00 AM" for a check. */
  when?: string;
  critical: boolean;
  /** Set when non-critical work is being deferred and this card is showing the waiting state (N2). */
  deferredLine?: string;
  /** The one primary button (N4). */
  primary: { label: string; action: NextAction };
  /** A check's due and late instants, for the deck's time indicator (core deck.ts). Absent for gates and problems. */
  dueAt?: IsoDateTime;
  lateAt?: IsoDateTime;
  /** The place's first day: nothing is late (schedule.ts firstDay). */
  firstDay?: true;
  /** The room or thing, in plain words ("Cloth bucket"), when the check names one. */
  where?: string;
  /** Delegation (delegate.ts): who asked, or whose it is. Absent: anyone on shift (A1). */
  assign?: NextAssign;
  /** Given to someone else: the button is "I'll do it" and the app records the step-in (delegate.ts A6) before it opens the task. */
  stepIn?: { key: string };
}

export interface NextResult {
  next: NextItem;
  /** Everything else still open today (N5): other problems and other unfinished checks. */
  more: number;
  /** Ids that are still open (the Next's own included when it is a task). For "was that one just finished?". */
  openIds: string[];
}

export interface NextInput {
  now: string;
  moment: Moment;
  role: FeedRole;
  /** A staff person in charge sees the open problems (feed.ts `inCharge`). */
  inCharge?: boolean;
  /** People on today's shift list. Undefined: unknown, so nothing is deferred. */
  onShiftCount?: number;
  /** SEAM, no source yet: a real rush signal. Only changes the wording of the deferral line. */
  busy?: boolean;
  gate?: { ready: boolean; blocking: { kind: string; title: string }[] };
  hasCheckin?: boolean;
  /** Checked in, but told not to work today (a stop-work): the worker line to show. */
  stopWork?: { line: string };
  expected: (ExpectedCheck & { satisfied?: boolean })[];
  /** Who each check was given to, by check key, as THIS person reads it (delegate.ts assignViewOf). Absent or missing a key: anyone on shift. */
  assign?: Record<string, NextAssign | undefined>;
  problems: NextProblem[];
  /** "10:00 AM" from an ISO instant, in the location's zone. Supplied by the app (it owns the zone). */
  timeLabel: (iso: string) => string;
  /** The button words for a check ("Log fridge temps"). Supplied by the app (web and phone word them alike). */
  verb: (e: ExpectedCheck) => string;
}

export const isCriticalCheck = (e: Pick<ExpectedCheck, "templateId">) => CRITICAL_TEMPLATES.has(e.templateId);

export function isLowStaff(onShiftCount: number | undefined): boolean {
  return typeof onShiftCount === "number" && onShiftCount <= LOW_STAFF_MAX;
}

/** Service = the in-hours part of the day. Opening, closing and evening are not service (cleaning is the point of them). */
export const isService = (moment: Moment) => moment === "day";

const lower = (s: string) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);
/** "Wipe the counters." → "wipe the counters". */
const phrase = (s: string) => lower(s.replace(/[.\s]+$/, ""));

function glyphOf(e: Pick<ExpectedCheck, "templateId" | "taskId">): NextGlyph {
  if (e.templateId === "sanitizer" || e.templateId === "wiping_cloths") return "flask";
  if (e.templateId === "restroom_check") return "restroom";
  if (e.templateId === "task_done") return "do";
  return "thermo";
}

function whyOf(e: ExpectedCheck, when: string, late: boolean): string {
  const reason = REASON[e.templateId] ?? (e.howTo?.why ? e.howTo.why.split(/(?<=[.!?])\s/)[0]!.replace(/\.$/, "") : e.hint ? e.hint.replace(/\.$/, "") : "");
  const time = late ? `was due ${when}` : `due ${when}`;
  return `${e.label.replace(/[.\s]+$/, "")}: ${time}${reason ? `, ${lower(reason)}` : ""}.`;
}

function checkItem(e: ExpectedCheck, i: NextInput): NextItem {
  const late = isLate(e, i.now);
  const at = i.timeLabel(e.dueAt);
  const a = i.assign?.[e.key];
  const item: NextItem = {
    id: e.key, kind: "check", tone: late ? "warn" : "info", glyph: glyphOf(e), title: e.label.replace(/[.\s]+$/, ""),
    why: whyOf(e, at, late), when: late ? `Was due ${at}` : at, critical: isCriticalCheck(e),
    dueAt: e.dueAt, lateAt: e.lateAt, ...(e.firstDay ? { firstDay: e.firstDay } : {}),
    ...((e.cooling?.food || WHERE[e.templateId]) ? { where: e.cooling?.food ?? WHERE[e.templateId]! } : {}),
    // A cooling batch's reading opens that batch, and says which reading (core cooling.ts).
    primary: { label: e.cooling ? `Take the ${e.cooling.stage === "2h" ? "2-hour" : "6-hour"} reading` : i.verb(e), action: { kind: "check", key: e.key, templateId: e.templateId, ...(e.taskId ? { taskId: e.taskId } : {}), ...(e.cooling ? { coolingId: e.cooling.entryId } : {}) } },
  };
  if (!a) return item;
  // Delegation (N6): mine says who asked; someone else's says whose it is, and the button steps in.
  return a.mine ? { ...item, assign: a } : { ...item, assign: a, stepIn: { key: e.key }, primary: { ...item.primary, label: `I'll do it: ${lower(item.primary.label)}` } };
}

function problemItem(p: NextProblem): NextItem {
  return { id: p.id, kind: "problem", tone: p.critical ? "stop" : "warn", glyph: p.critical ? "alert" : "write", title: p.title, why: p.why, critical: p.critical, primary: { label: p.verb, action: p.action } };
}

/** The deferral line (N2). "After the rush" only when a real rush signal was passed; otherwise the plain truth. */
export function deferredLineFor(e: ExpectedCheck, i: Pick<NextInput, "busy" | "timeLabel">): string {
  const by = i.timeLabel(e.lateAt);
  return `${i.busy ? "After the rush" : "Can wait"}: ${phrase(e.label)} (due by ${by})`;
}

/** Where an item sits in the order (N1/N2). The deck reads the same order the Next card does. */
export type NextBucket = "stop_work" | "gate" | "food_risk" | "crit_problem" | "crit_late" | "crit_soon" | "assigned" | "problem" | "late" | "soon" | "waiting" | "upcoming" | "done";

/** One place in the order: the plain item, its bucket, and the id it covers for the "more" count. */
export interface RankedNext { item: NextItem; bucket: NextBucket; taken: string[] }

/** An item's own id: the Next card prefixes a waiting or upcoming one ("waiting:<key>"); the deck never does. */
export const realNextId = (id: string) => id.replace(/^(waiting|upcoming):/, "");

/**
 * THE ORDER (N1, N2): everything still open, most pressing first. `chooseNext` is its first entry and the card deck
 * (core deck.ts) is all of it, so the deck can never disagree with the Next card. A stop-work or an open gate is the
 * whole list (nothing else can be done before it). Otherwise: open safety problems, late then soon critical checks,
 * other problems, late checks, checks due within the hour, then the deferred ones (low staff in service), then what is
 * due later today, in due order. Nothing open: one "done" entry.
 */
export function rankNext(i: NextInput): RankedNext[] {
  const open = i.expected.filter((e) => !e.satisfied);
  // A stop-work is not negotiable and not deferred: the card is the fact, and the button reopens the check-in.
  if (i.stopWork) {
    return [{ bucket: "stop_work", taken: [], item: { id: "stop-work", kind: "stop_work", tone: "stop", glyph: "hands", title: "No food work today", why: i.stopWork.line, critical: true, primary: { label: "Answer again", action: { kind: "checkin" } } } }];
  }
  // The gate: nothing else can be done before it (feed.ts staffBlocked), so it is the one thing.
  if (i.gate && !i.gate.ready) {
    const needsCheckin = !i.hasCheckin;
    return [{ bucket: "gate", taken: [], item: {
      id: "gate", kind: "gate", tone: "stop", glyph: needsCheckin ? "hands" : "learn", critical: true,
      title: needsCheckin ? "Check in before you touch food" : "Finish your lessons first",
      why: needsCheckin ? "A few quick health questions, then wash your hands. About 30 seconds." : (i.gate.blocking[0]?.title ?? "A lesson or sign-off is still open."),
      primary: needsCheckin ? { label: "Start my check-in", action: { kind: "checkin" } } : { label: "Finish my lessons", action: { kind: "learn" } },
    } }];
  }

  const soon = Date.parse(i.now) + 60 * 60_000;
  // N6: mine first, then unassigned, then someone else's (a stable sort: each keeps its due order inside its group).
  const mineOf = (e: ExpectedCheck) => !!i.assign?.[e.key]?.mine;
  const theirsOf = (e: ExpectedCheck) => !!i.assign?.[e.key] && !i.assign[e.key]!.mine;
  const rankMine = (a: ExpectedCheck, b: ExpectedCheck) => Number(mineOf(b)) - Number(mineOf(a)) || Number(theirsOf(a)) - Number(theirsOf(b));
  const byDue = (a: ExpectedCheck, b: ExpectedCheck) => rankMine(a, b) || a.dueAt.localeCompare(b.dueAt);
  const dueNow = open.filter((e) => Date.parse(e.dueAt) <= soon).sort(byDue);
  const defer = isService(i.moment) && isLowStaff(i.onShiftCount);
  // N1b: food at risk first, then the other safety problems.
  const riskProblems = i.problems.filter((p) => p.foodRisk);
  const critProblems = i.problems.filter((p) => p.critical && !p.foodRisk);
  const otherProblems = i.problems.filter((p) => !p.critical && !p.foodRisk);
  const critLate = dueNow.filter((e) => isCriticalCheck(e) && isLate(e, i.now));
  const critSoon = dueNow.filter((e) => isCriticalCheck(e) && !isLate(e, i.now));
  // Someone asked me (N6): not deferred, and ahead of the other open work. Only what is due within the hour or late: a task for
  // later today leads the "later" list instead (below), so nothing is pulled forward that is not yet due.
  const assignedMine = dueNow.filter((e) => !isCriticalCheck(e) && mineOf(e));
  const restLate = dueNow.filter((e) => !isCriticalCheck(e) && !mineOf(e) && isLate(e, i.now));
  const restSoon = dueNow.filter((e) => !isCriticalCheck(e) && !mineOf(e) && !isLate(e, i.now));
  const out: RankedNext[] = [];
  const check = (bucket: NextBucket, e: ExpectedCheck, patch?: Partial<NextItem>) => out.push({ bucket, taken: [e.key], item: { ...checkItem(e, i), ...patch } });
  const problem = (bucket: NextBucket, p: NextProblem) => out.push({ bucket, taken: [p.id], item: problemItem(p) });

  // N1 first (they insist even in service), then the rest in order: problems, late, due soon. Deferral (N2) only trims restSoon.
  for (const p of riskProblems) problem("food_risk", p);
  for (const p of critProblems) problem("crit_problem", p);
  for (const e of critLate) check("crit_late", e);
  for (const e of critSoon) check("crit_soon", e);
  for (const e of assignedMine) check("assigned", e);
  for (const p of otherProblems) problem("problem", p);
  for (const e of restLate) check("late", e);
  // restSoon holds only non-critical checks that are not yet late (critical and late ones were taken above), so a
  // deferral can never hide a critical check or outlast a deadline.
  if (!defer) for (const e of restSoon) check("soon", e);
  // Only deferrable work: say so truthfully, and keep the button honest (do it now if you have a minute).
  else for (const e of restSoon) check("waiting", e, { kind: "waiting", tone: "info", why: deferredLineFor(e, i), deferredLine: deferredLineFor(e, i) });
  // Nothing due within the hour: the rest of today, in due order.
  for (const e of open.filter((x) => Date.parse(x.dueAt) > soon).sort(byDue)) check("upcoming", e, { kind: "upcoming", tone: "go", critical: false });

  if (!out.length) out.push({ bucket: "done", taken: [], item: { id: "done", kind: "done", tone: "go", glyph: "check", title: "Everything for today is done", why: "Thank you. If something looks wrong, tell us now.", critical: false, primary: { label: "Report a problem", action: { kind: "report" } } } });
  return out;
}

export function chooseNext(i: NextInput): NextResult {
  const openIds = [...i.expected.filter((e) => !e.satisfied).map((e) => e.key), ...i.problems.map((p) => p.id)];
  const first = rankNext(i)[0]!;
  const finish = (next: NextItem): NextResult => ({ next, more: openIds.filter((id) => !first.taken.includes(id)).length, openIds });
  const item = first.item;
  // The first of the deferred ones: "Nothing urgent right now", and the button says "Do it now".
  if (first.bucket === "waiting") return finish({ ...item, id: `waiting:${item.id}`, kind: "waiting", tone: "info", glyph: "clock", title: "Nothing urgent right now", critical: false, primary: { label: `Do it now: ${lower(item.primary.label)}`, action: item.primary.action } });
  // The first of what is due later: "Nothing due until 3:00 PM", and the button says "Do it early".
  if (first.bucket === "upcoming") {
    const e = i.expected.find((x) => x.key === item.id)!;
    return finish({ ...item, id: `upcoming:${item.id}`, kind: "upcoming", tone: "go", glyph: "clock", title: `Nothing due until ${i.timeLabel(e.dueAt)}`, why: `Next: ${phrase(e.label)}.`, critical: false, primary: { label: `Do it early: ${lower(item.primary.label)}`, action: item.primary.action } });
  }
  return finish(item);
}

/**
 * The open problems for the Next card, from the feed's own cards (so the rule of who sees what stays in feed.ts):
 * a failed reading (critical when a failed rule is a safety one, nag.ts URGENT_RULES), an open report (critical when
 * urgent), and a manager's "please fix this" note. Missed-check findings are left out: the check itself is the item.
 */
export function problemsFromCards(cards: readonly FeedCard[]): NextProblem[] {
  const out: NextProblem[] = [];
  for (const c of cards) {
    if (c.kind === "fix_ask") out.push({ id: c.id, title: c.headline, why: "Your manager asked you to look at this record again.", critical: false, verb: "Write down the fix", action: { kind: "fix", entryId: c.entryId, askId: c.id.replace(/^fix-/, "") } });
    else if (c.kind === "finding") {
      const f = c.finding;
      if (f.kind === "failed_check" && f.ref) {
        const safety = urgentProblem(f.ruleIds ?? []);
        out.push({ id: `problem:${f.ref}`, title: f.title.replace(/^Needs a fix:\s*/, ""), why: safety ? `${safety[0]!.toUpperCase()}${safety.slice(1)}. Write what you did about it.` : "Write what you did about it.", critical: !!safety, ...(isFoodAtRiskFinding(f) ? { foodRisk: true } : {}), verb: "Add what was done", action: { kind: "fix", entryId: f.ref } });
      } else if (f.kind === "incident" && f.ref) {
        out.push({ id: `problem:${f.ref}`, title: f.title, why: f.incidentType ? nextStepFor(f.incidentType).line : f.severity === "urgent" ? "An urgent report is still open. Is it still there?" : "A report is still open. Is it still there?", critical: f.severity === "urgent", verb: "Open the report", action: { kind: "incident", id: f.ref } });
      }
    } else if (c.kind === "throw_out") {
      // Food past its date or its time (feed.ts F1): Food Code 3-501.18(A) "shall be discarded".
      out.push({ id: `food:${c.food.id}`, title: `Throw out ${c.food.name.toLowerCase()}`, why: `${c.food.reason} Throw it out and log it (Food Code 3-501.18).`, critical: true, foodRisk: true, verb: "Throw it out", action: { kind: "throw_out", foodId: c.food.id } });
    }
  }
  return out;
}

/** True when a feed card is the very thing the Next card shows, so the fold under it does not list it twice. */
export function coversCard(next: Pick<NextItem, "id" | "kind" | "primary">, c: FeedCard): boolean {
  switch (next.kind) {
    case "gate": case "stop_work": return c.kind === "gate";
    case "check": case "waiting": case "upcoming": { const a = next.primary.action; return c.kind === "check" && a.kind === "check" && c.check.key === a.key; }
    case "problem": return (c.kind === "fix_ask" && c.id === next.id) || (c.kind === "finding" && c.finding.ref !== undefined && next.id === `problem:${c.finding.ref}`) || (c.kind === "throw_out" && next.id === `food:${c.food.id}`);
    default: return false;
  }
}

/**
 * "That one is done": the item shown last time is no longer open and is not the Next now. Null when it is still open
 * (or was seen long ago), so the check-draw animation plays only for real completions.
 */
export function justFinished(prev: { id: string; title: string; at: number } | undefined, res: Pick<NextResult, "next" | "openIds">, nowMs: number, maxAgeMs = 30 * 60_000): string | null {
  if (!prev || nowMs - prev.at > maxAgeMs) return null;
  if (prev.id === res.next.id) return null;
  const realId = realNextId;
  // A stop-work is not something you finish, and "all done" has nothing to finish.
  if (prev.id === "stop-work" || prev.id === "done") return null;
  // The gate is finished once it is no longer the card, unless the check-in turned into a stop-work.
  if (prev.id === "gate") return res.next.id === "stop-work" ? null : prev.title;
  if (res.openIds.includes(realId(prev.id))) return null;
  return prev.title;
}
